import { useState } from "react";
import { MedicineDoseDto, medicineDoseFormData } from "../types/medicineDose";
import { medicineDoseService } from "../services/medicineDoseService";
import { useApi } from "./useApi";
import { useMutation } from "./useMutation";

export const useMedicineDoses = () => {
  // 1. Το GET γίνεται αυτόματα με το useApi, cache και signal
  const {
    data: medicineDoses = [],
    loading: isFetching,
    error: getError,
    refetch: fetchMedicineDoses,
    invalidateCache,
  } = useApi<MedicineDoseDto[]>("medicine-doses", (signal) => medicineDoseService.getAll({}, signal));

  // 2. Το CREATE
  const {
    execute: createExecution,
    loading: isCreating,
    error: createError,
  } = useMutation((newMedicineDose: typeof medicineDoseFormData) => medicineDoseService.create(newMedicineDose));

  // 2.1. Το UPDATE (Προσθήκη)
  const {
    execute: updateExecution,
    loading: isUpdating,
    error: updateError,
  } = useMutation(({ id, data }: { id: number; data: typeof medicineDoseFormData }) => 
    medicineDoseService.update(id, data)
  );

  // 3. Το DELETE
  const {
    execute: deleteExecution,
    loading: isDeleting,
    error: deleteError,
  } = useMutation(async (ids: number[]) => {
    await Promise.all(ids.map((id) => medicineDoseService.delete(id)));
    return ids;
  });

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "success",
  });

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const createMedicineDose = async (newMedicineDose: typeof medicineDoseFormData): Promise<void> => {
    try {
      await createExecution(newMedicineDose);
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η δόση φαρμάκου αποθηκεύτηκε επιτυχώς!",
        type: "success",
      });
      fetchMedicineDoses(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα δημιουργίας";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  // Συνάρτηση Update
  const updateMedicineDose = async (id: number, updatedData: typeof medicineDoseFormData): Promise<void> => {
    try {
      await updateExecution({ id, data: updatedData });
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η ενημέρωση ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      fetchMedicineDoses(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα ενημέρωσης";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const deleteMedicineDoses = async (ids: number[]): Promise<void> => {
    try {
      await deleteExecution(ids);
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η διαγραφή ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      fetchMedicineDoses(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα κατά τη μαζική διαγραφή";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  return {
    medicineDoses,
    isLoading: isFetching || isCreating || isUpdating || isDeleting,
    error: getError || createError || updateError || deleteError,
    fetchMedicineDoses: () => fetchMedicineDoses(true),
    createMedicineDose,
    updateMedicineDose, // <-- Το επιστρέφουμε κανονικά εδώ
    deleteMedicineDoses,
    modalState,
    closeModal,
  };
};